'use client'

import { memo, useEffect, useMemo, useRef } from 'react'

import {
  DEFAULT_FRAME_H,
  DEFAULT_FRAME_W,
  DEFAULT_FRAMES,
  DEFAULT_LOOP_MS,
  DEFAULT_SCALE,
  DEFAULT_STATE_ROWS,
  ROW_TO_STATE,
  STATE_ALIASES
} from './constants'
import type { PetInfo, PetState } from './types'

interface PetCanvasSpriteProps {
  info: PetInfo
  state?: PetState
  rowOverride?: string
  zoom?: number
}

function PetCanvasSpriteImpl({ info, state = 'idle', rowOverride, zoom = 1 }: PetCanvasSpriteProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const stateRef = useRef<PetState>(state)
  const rowOverrideRef = useRef<string | undefined>(rowOverride)

  useEffect(() => {
    stateRef.current = state
  }, [state])

  useEffect(() => {
    rowOverrideRef.current = rowOverride
  }, [rowOverride])

  const frameW = info.frameW ?? DEFAULT_FRAME_W
  const frameH = info.frameH ?? DEFAULT_FRAME_H
  const frames = info.framesPerState ?? DEFAULT_FRAMES
  const loopMs = info.loopMs ?? DEFAULT_LOOP_MS
  const scale = (info.scale ?? DEFAULT_SCALE) * zoom
  const rows = info.stateRows ?? DEFAULT_STATE_ROWS

  const drawW = Math.round(frameW * scale)
  const drawH = Math.round(frameH * scale)

  const image = useMemo(() => {
    if (!info.src || typeof window === 'undefined') return null
    const img = new window.Image()
    img.src = info.src
    return img
  }, [info.src])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !image) return

    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return

    let raf = 0
    let frame = 0
    let lastStep = performance.now()
    let drawnFrame = -1
    let drawnRow = -1
    let activeRow = -1
    let activeCount = -1

    const rowIndexForState = (s: PetState): number => {
      for (const key of STATE_ALIASES[s] ?? [s]) {
        const idx = rows.indexOf(key)
        if (idx >= 0) return idx
      }
      return 0
    }

    const resolve = (s: PetState): { row: number; count: number } => {
      return { row: rowIndexForState(s), count: frames }
    }

    const resolveRow = (rowName: string): { row: number; count: number } => {
      const row = rows.indexOf(rowName)
      const st = ROW_TO_STATE[rowName]
      return { row: row >= 0 ? row : rowIndexForState(st ?? 'idle'), count: frames }
    }

    const render = (now: number) => {
      const forcedRow = rowOverrideRef.current
      const { row, count } = forcedRow ? resolveRow(forcedRow) : resolve(stateRef.current)

      if (row !== activeRow || count !== activeCount) {
        activeRow = row
        activeCount = count
        frame = 0
        lastStep = now
        drawnFrame = -1
      }

      const stepMs = loopMs / count
      if (now - lastStep >= stepMs) {
        frame += 1
        lastStep = now
      }

      frame %= count

      if ((frame !== drawnFrame || row !== drawnRow) && image.complete && image.naturalWidth > 0) {
        const sx = frame * frameW
        const sy = row * frameH
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.imageSmoothingEnabled = false
        ctx.drawImage(image, sx, sy, frameW, frameH, 0, 0, drawW, drawH)
        drawnFrame = frame
        drawnRow = row
      }

      raf = requestAnimationFrame(render)
    }

    raf = requestAnimationFrame(render)

    return () => {
      cancelAnimationFrame(raf)
    }
  }, [image, frameW, frameH, frames, loopMs, drawW, drawH, rows])

  return (
    <canvas
      aria-label={info.displayName ? `${info.displayName} sprite` : 'hermes sprite'}
      height={drawH}
      ref={canvasRef}
      style={{ height: drawH, width: drawW }}
      width={drawW}
    />
  )
}

export const PetCanvasSprite = memo(PetCanvasSpriteImpl)
